import { Grid, GridCol, Modal, TextInput } from "@mantine/core"
import { showNotification } from "@mantine/notifications"
import { Button } from "@tfg/ui"
import { Dispatch, SetStateAction, useState } from "react"
import { useTranslation } from "react-i18next"
import z from "zod"

import {
  CreateBusinessPartnerRequestDto,
  UpdateBusinessPartnerRequestDto,
} from "~/gen/api/types"
import { SUPPLIER_INITIAL_VALUES } from "~/pages/Brand/Suppliers/SuppliersRepository/config"
import { useCreateContactByEmailMutation } from "~/pages/Brand/Suppliers/SuppliersRepository/hooks/useCreateContactByEmailMutation"

type Props = {
  opened: boolean
  onClose: () => void
  openDrawer: () => void
  setInitialValues: Dispatch<
    SetStateAction<
      CreateBusinessPartnerRequestDto | UpdateBusinessPartnerRequestDto
    >
  >
  searchSupplier: string
}

export function SupplierCreationModal({
  opened,
  onClose,
  openDrawer,
  setInitialValues,
  searchSupplier,
}: Props) {
  const [email, setEmail] = useState("")
  const [emailError, setEmailError] = useState<string | null>(null)

  const { t } = useTranslation("organizationSettings")

  const { mutateAsync: createSupplierMappingByEmail } =
    useCreateContactByEmailMutation({
      searchSupplier,
    })

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const email = z.email().safeParse(event.currentTarget.value)
    if (email.success) {
      setEmailError(null)
    } else {
      setEmailError(t("supplierSettings.invalidEmail"))
    }
    setEmail(email.data ?? event.currentTarget.value)
  }

  const isEmailValid = email.trim() !== "" && emailError === null

  const handleCreateSupplierMappingByEmail = async () => {
    if (!isEmailValid) return

    const { data } = await createSupplierMappingByEmail({
      data: { email },
    })
    if (data) {
      showNotification({
        title: "Supplier created",
        message: `Supplier created successfully`,
        color: "teal",
      })
      setEmail("")
      onClose()
    } else {
      setInitialValues((prev) => ({
        ...prev,
        ...SUPPLIER_INITIAL_VALUES,
        email,
      }))
      setEmail("")
      onClose()
      openDrawer()
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t("supplierSettings.addNewSupplier")}
      p="md"
      centered
    >
      <Modal.Body p={0}>
        <Grid w="100%" pr="md">
          <GridCol span={10}>
            <TextInput
              value={email}
              onChange={handleEmailChange}
              placeholder={t("supplierSettings.supplierEmail")}
              error={emailError}
            />
          </GridCol>
          <GridCol span={2}>
            <Button
              disabled={!isEmailValid}
              onClick={handleCreateSupplierMappingByEmail}
            >
              {t("supplierSettings.submit")}
            </Button>
          </GridCol>
        </Grid>
      </Modal.Body>
    </Modal>
  )
}
